<template>
  <div class="k-carousel overflow-hidden relative-position q-px-20">
    <div class="k-carousel__box overflow-hidden">
      <div
        class="k-carousel__box-slides row no-wrap justify-start items-start gap-12"
        :class="slides.length > 3 ? 'justify-start' : 'justify-center'"
        :style="{
          transform: `translateX(-${imageLocation}px`,
        }"
      >
        <div
          v-for="(slide, index) in slides"
          :key="index"
          class="k-carousel__box-slide overflow-hidden cursor-pointer relative-position rounded-8"
          :class="{
            'k-carousel__box-slide--selected': index === activeImageRef,
          }"
          draggable="true"
          @dragstart="draggedIndexRef = index"
          @dragover.prevent
          @drop="drop(index)"
          @mouseover="hoveredIndexRef = index"
          @click="showActiveSlide(index)"
        >
          <q-img class="rounded-8" object-fit="cover" :src="slide" alt="Slide" width="88px" height="56px" />

          <div
            class="k-carousel__box-image-overlay rounded-8 cursor-pointer full-heigh absolute row justify-between items-center"
          >
            <div class="k-carousel__box-image-overlay-reorder row justify-center items-center" @click.stop>
              <KIcon name="arrangement" class="cursor-pointer" color="cool-gray-70" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="slides.length > 3"
      class="k-carousel__navigation text-center row items-center justify-center q-mt-md gap-lg"
    >
      <KIcon name="arrow-left" :color="leftArrowColor" class="cursor-pointer" size="26px" @click="prevSlide" />
      <div class="k-carousel__navigation-bullets-container row items-center gap-12">
        <div
          v-for="(slide, index) in Math.ceil(slides.length / 3)"
          :key="index"
          class="k-carousel__navigation-bullets-container-bullet inline cursor-pointer rounded-circle q-mx-xs"
          :class="Math.floor(activeImageRef / 3) + 1 === index + 1 ? 'bg-space-b-100' : 'bg-cool-gray-50'"
          @click="goToSlide(index)"
        ></div>
      </div>
      <KIcon name="arrow-right" :color="rightArrowColor" class="cursor-pointer" size="26px" @click="nextSlide" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, toValue, watchEffect } from 'vue'

defineOptions({ name: 'KCarousel' })

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  sort: { type: Array, default: () => [] },
  activeImage: { type: Number, default: 0 },
})

const emit = defineEmits(['update:model-value', 'update:sort', 'update:active-image'])

const hoveredIndexRef = ref(-1)
const draggedIndexRef = ref(null)
const uploadedImages = toValue(() => props.modelValue)
const locationIndex = ref(0)
const sortRef = ref(props.sort)
const activeImageRef = ref(props.activeImage)

const imageWidth = 88
const imageGap = 12

const leftArrowColor = computed(() => (!activeImageRef.value ? 'cool-gray-50' : 'cool-gray-100'))

const imageLocation = computed(() => {
  if (activeImageRef.value > 2) return (slides.value.length - 4) * (imageWidth + imageGap) + imageWidth / 2
  else return 0
})

const slides = computed(() => {
  if (uploadedImages.length > 0) {
    const images = []
    uploadedImages.forEach((image) => {
      if (typeof image === 'string') images.push(image)
      else images.push(URL.createObjectURL(image))
    })
    return images
  }

  return uploadedImages
})

const rightArrowColor = computed(() =>
  activeImageRef.value > Math.round(slides.value.length / 3) ? 'cool-gray-50' : 'cool-gray-100',
)

const goToSlide = (index) => {
  if (index === 0) activeImageRef.value = 0
  else activeImageRef.value = slides.value.length - 1
  emit('update:active-image', activeImageRef.value)
}

const showActiveSlide = (index) => {
  activeImageRef.value = index
  emit('update:active-image', activeImageRef.value)
}

const nextSlide = () => {
  if (activeImageRef.value < slides.value.length - 1) {
    locationIndex.value = slides.value.length - 1

    activeImageRef.value = locationIndex.value
    emit('update:active-image', activeImageRef.value)
  }
}

const prevSlide = () => {
  if (activeImageRef.value > 0) {
    activeImageRef.value = 0
    emit('update:active-image', activeImageRef.value)
  }
}

const handleDropEvent = (arr, index) => {
  const newArray = arr
  const dragged = newArray[draggedIndexRef.value]
  newArray.splice(draggedIndexRef.value, 1)
  newArray.splice(index, 0, dragged)
  return newArray
}

const drop = (index) => {
  if (draggedIndexRef.value === null) return
  const newSlides = handleDropEvent(uploadedImages, index)
  const newSort = handleDropEvent(sortRef.value, index)
  draggedIndexRef.value = null
  emit('update:model-value', newSlides)
  emit('update:sort', newSort)
}

watchEffect(() => {
  activeImageRef.value = props.activeImage
  if ((activeImageRef.value + 1) % 4 === 0) locationIndex.value = activeImageRef.value
  emit('update:active-image', activeImageRef.value)
})
</script>

<style scoped lang="sass">
.k-carousel
  width: 388px
  &__box
    width:348px
  &__box-slides
    width: 348px
    transition: transform 0.3s ease-in-out
  &__box-slide
    width:88px
    min-width: 88px
    height:56px
    min-height: 56px
    border: 1.5px solid var(--cool-gray-30)
    &:hover
      .k-carousel__box-image-overlay
        opacity: 1

    img
      width: 88px
      min-width: 88px
      height: 56px
      min-height: 56px
    &:hover
      border: 1.5px solid var(--space-b-50)
    &--selected
      border: 1.5px solid var(--space-b-70)
  &__box-image-overlay
    top: 0px
    left: 0px
    opacity: 0
    transition: opacity 0.3s
  &__box-image-overlay-reorder
    height: 57px
    background-color: rgba(255, 255, 255, 0.90)
  &__navigation-bullets-container-bullet
    width: 6px
    height: 6px
    transition: background-color 0.3s ease-in-out
</style>
